import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, CardHeader } from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import TableContainer from "components/common/TableContainer";
import { DateOnly } from "components/common/Date";
import { formatQuantity } from "utils/quantity";
import {
  SOURCE_TYPE_URL_MAP,
  type AnySourceType,
} from "constants/source-type.constants";
import { MaterialInOutSourceType } from "features/MaterialInOuts/constants/material-in-out.constants";
import type { MaterialInOut } from "features/MaterialInOuts/types/material-in-out.types";
import { getMaterialsOut } from "features/MaterialInOuts/services/MaterialInOutService";

function concernedPersonName(row: MaterialInOut): string {
  const { firstName, lastName } = row.user ?? {};
  return [firstName, lastName].filter(Boolean).join(" ").trim() || "—";
}

function WorkCell({ row }: { row: MaterialInOut }) {
  const isOther = row.sourceType === MaterialInOutSourceType.OTHER;
  const label =
    (isOther ? row.otherDescription : row.workLabel)?.trim() ||
    row.workLabel ||
    "—";

  if (!isOther && row.sourceId != null) {
    const base = SOURCE_TYPE_URL_MAP[String(row.sourceType) as AnySourceType];
    if (base) {
      return (
        <Link
          to={`${base}/${row.sourceId}`}
          className="text-decoration-none link"
        >
          {label}
        </Link>
      );
    }
  }

  return <span>{label}</span>;
}

export default function MaterialsOutDashboardCard() {
  const columns: ColumnDef<MaterialInOut>[] = useMemo(
    () => [
      {
        header: "Material",
        id: "material",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="fw-medium">{row.original.material?.name ?? "—"}</span>
        ),
      },
      {
        header: "Concerned Person",
        id: "employee",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="small text-muted">
            {concernedPersonName(row.original)}
          </span>
        ),
      },
      {
        header: "Work",
        id: "work",
        enableColumnFilter: false,
        cell: ({ row }) => <WorkCell row={row.original} />,
      },
      {
        header: "Date Out",
        id: "dateOut",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="small text-muted">
            <DateOnly date={row.original.dateOut} />
          </span>
        ),
      },
      {
        header: "Quantity",
        id: "quantity",
        enableColumnFilter: false,
        cell: ({ row }) => formatQuantity(row.original.quantity),
      },
    ],
    [],
  );

  return (
    <Card className="mb-4">
      <CardHeader className="d-flex align-items-center justify-content-between">
        <h5 className="card-title mb-0">Materials Out</h5>
        <Link
          to="/inventory-management/material-in-outs"
          className="btn btn-sm btn-soft-primary"
        >
          View all
        </Link>
      </CardHeader>
      <CardBody>
        <TableContainer
          columns={columns}
          fetchApi={getMaterialsOut}
          syncPaginationToUrl={false}
          pageSize={10}
          globalSearch={false}
          hidePageSize
          divClass="table-responsive table-card mb-0"
          tableClass="table-nowrap align-middle mb-0"
          theadClass="table-light text-muted"
        />
      </CardBody>
    </Card>
  );
}
